// @ts-nocheck
// app/chat.tsx
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Pressable,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  ListRenderItemInfo,
  Image,
  Modal,
  ActivityIndicator,
  ScrollView,
  Animated,
  LayoutAnimation,
  UIManager,
  I18nManager,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { useNavigation } from '@react-navigation/native';
import { Send, Mail, X } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { Message, Course, LeadData, ParsedQuizContext } from '@/types/chat';
import { LazyChatMessage } from '@/components/LazyChatMessage';
import { LazyCoursesGrid } from '@/components/LazyCoursesGrid';
import { LazyLeadModal } from '@/components/LazyLeadModal';
import { LazyBookCallModal } from '@/components/LazyBookCallModal';
import { LazyCodeRainBackground } from '@/components/LazyCodeRainBackground';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { QuickReplies } from '@/components/QuickReplies';
import { Toast } from '@/components/Toast';
import { TypingIndicator } from '@/components/TypingIndicator';
import { ChatHeader } from '@/components/ChatHeader';
import { ConversationsListModal } from '@/components/ConversationsListModal';
import { ProgressIndicator } from '@/components/ProgressIndicator';
import { EmailCaptureModal } from '@/components/EmailCaptureModal';
import { SimpleCoursesModal } from '@/components/SimpleCoursesModal';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { useChat } from '@/features/chat/useChat';
import { useChatStyles } from '@/features/chat/useChatStyles';
import { useChatAutoScroll } from '@/features/chat/useChatAutoScroll';
import { supabase } from '@/utils/supabase';
import { courseRequestService } from '@/utils/courseRequestService';
import { leadSubmissionService } from '@/utils/leadSubmissionService';
import {
  trackPageView,
  trackButtonClick,
  trackFunnelStep,
} from '@/utils/analytics';
import { usePageEngagement } from '@/hooks/usePageEngagement';
import {
  QUICK_FLOW_TOTAL_STEPS,
  type QuickFlowOption,
  type QuickFlowStateSnapshot,
  clearQuickFlowState,
  generateDynamicStep,
  getInitialQuickFlowSnapshot,
  loadQuickFlowState,
  persistQuickFlowState,
  runQuickFlowRecommendation,
} from '@/features/chat/quickFlow';

const MAX_MESSAGE_LENGTH = 500;

type MessageItem =
  | { type: 'message'; data: Message }
  | { type: 'courses'; data: Course[] }
  | { type: 'emailButton' }
  | { type: 'typing' };

const RESUME_LEAD_GATE_TEXT = 'המשך השארת פרטים';

type PendingExit =
  | { kind: 'goBack'; action?: any }
  | { kind: 'home' }
  | { kind: 'closeLeadGate' };

type EntryMode = 'regular' | 'quick' | null;

function normalizeEmail(raw: string): string {
  return (raw ?? '').toString().trim().toLowerCase();
}

function isValidEmail(email: string): boolean {
  const e = normalizeEmail(email);
  return e.length >= 5 && e.includes('@') && e.includes('.');
}

function getSingleParam(params: any, key: string): string | undefined {
  const raw = params?.[key];
  if (raw == null) return undefined;
  return Array.isArray(raw) ? raw[0] : raw;
}

function isTruthyParam(v: any): boolean {
  const s = (v ?? '').toString().trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes' || s === 'y';
}

export default function ChatScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const params = useLocalSearchParams();
  const { settings } = useAccessibility();
  const initialMessageProcessed = useRef(false);

  const headerIntroInjectedRef = useRef(false);
  const quizIntroInjectedRef = useRef(false);
  const campaignRedirectDoneRef = useRef(false);

  const allowNextGoBackRef = useRef(false);
  const exitPromptShownOnceRef = useRef(false);
  const exitPromptDismissedRef = useRef(false);
  const exitSummarySentRef = useRef(false);
  const exitEmailDraftRef = useRef('');

  const [showExitSummaryPrompt, setShowExitSummaryPrompt] = useState(false);
  const [showExitEmailModal, setShowExitEmailModal] = useState(false);
  const [pendingExit, setPendingExit] = useState<PendingExit | null>(null);
  const [exitEmail, setExitEmail] = useState('');
  const [exitConsent, setExitConsent] = useState(false);
  const [exitEmailError, setExitEmailError] = useState<string | null>(null);
  const [exitSubmitting, setExitSubmitting] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().catch((err) => {
      console.error('[chat] Supabase auth error:', err);
    });
  }, []);

  useEffect(() => {
    if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  }, []);

  const fromParam = useMemo(() => {
    const raw = (params as any)?.from;
    return Array.isArray(raw) ? raw[0] : raw;
  }, [(params as any)?.from]);

  const entryParam = useMemo(() => {
    const v = getSingleParam(params as any, 'entry') || getSingleParam(params as any, 'start');
    return (v || '').toString().trim().toLowerCase();
  }, [params]);

  const campaignSourceParam = useMemo(() => {
    return (
      getSingleParam(params as any, 'source') ||
      getSingleParam(params as any, 'utm_source') ||
      getSingleParam(params as any, 'ref')
    );
  }, [params]);

  const campaignNameParam = useMemo(() => {
    return getSingleParam(params as any, 'campaign') || getSingleParam(params as any, 'utm_campaign');
  }, [params]);

  const campaignMediumParam = useMemo(() => {
    return getSingleParam(params as any, 'utm_medium');
  }, [params]);

  const campaignContentParam = useMemo(() => {
    return getSingleParam(params as any, 'utm_content');
  }, [params]);

  const campaignTermParam = useMemo(() => {
    return getSingleParam(params as any, 'utm_term');
  }, [params]);

  const campaignTopicParam = useMemo(() => {
    return getSingleParam(params as any, 'topic') || getSingleParam(params as any, 't');
  }, [params]);

  const skipWelcomeParam = useMemo(() => {
    const v = getSingleParam(params as any, 'skipWelcome');
    return isTruthyParam(v);
  }, [params]);

  const initialMessageParam = useMemo(() => {
    const raw =
      (params as any)?.initialMessage ??
      (params as any)?.message ??
      (params as any)?.q;
    return Array.isArray(raw) ? raw[0] : raw;
  }, [(params as any)?.initialMessage, (params as any)?.message, (params as any)?.q]);

  const quizSourceParam = useMemo(() => {
    const raw = (params as any)?.quizSource;
    return Array.isArray(raw) ? raw[0] : raw;
  }, [(params as any)?.quizSource]);

  const quizTrackTitleParam = useMemo(() => {
    const raw = (params as any)?.quizTrackTitle;
    return Array.isArray(raw) ? raw[0] : raw;
  }, [(params as any)?.quizTrackTitle]);

  const quizTrackParam = useMemo(() => {
    const raw = (params as any)?.quizTrack;
    return Array.isArray(raw) ? raw[0] : raw;
  }, [(params as any)?.quizTrack]);

  const isFromQuiz = quizSourceParam === 'quiz' || !!quizTrackParam;

  const fromHomeStart = fromParam === 'home_start';
  const fromHeader = fromParam === 'header';

  const isAdTraffic = useMemo(() => {
    const from = (fromParam || '').toString().trim().toLowerCase();
    const source = (campaignSourceParam || '').toString().trim().toLowerCase();
    const medium = (campaignMediumParam || '').toString().trim().toLowerCase();

    return (
      from.includes('instagram') ||
      from.includes('ad') ||
      source.includes('instagram') ||
      source.includes('meta') ||
      source.includes('facebook') ||
      medium === 'paid_social' ||
      medium === 'cpc' ||
      medium === 'paid'
    );
  }, [fromParam, campaignSourceParam, campaignMediumParam]);

  const homeStartIntro = fromHomeStart
    ? 'כתבו לי מה אתם רוצים ללמוד — ואני אמצא את הקורס המתאים ביותר.\n\nלמשל: "קורס פייתון למתחילים", "שיווק ברשתות חברתיות", "שוק ההון" ועוד.'
    : undefined;

  const shouldSkipWelcome =
    !isAdTraffic &&
    (fromHeader || !fromHomeStart) &&
    (skipWelcomeParam || !!initialMessageParam || fromHeader);

  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const [entryMode, setEntryMode] = useState<EntryMode>(null);
  const [quickFlow, setQuickFlow] = useState<QuickFlowStateSnapshot>(getInitialQuickFlowSnapshot());
  const [quickFlowLoading, setQuickFlowLoading] = useState(false);
  const [quickFlowSubmitting, setQuickFlowSubmitting] = useState(false);
  const [quickFlowError, setQuickFlowError] = useState<string | null>(null);
  const [quickFlowResult, setQuickFlowResult] = useState<{
    courses: Course[];
    matchCount: number;
    userSummary: string;
    searchData: any;
  } | null>(null);
  const quickStepAnim = useRef(new Animated.Value(1)).current;
  const quickStepTranslateX = useRef(new Animated.Value(0)).current;
  const quickStepOpacity = useRef(new Animated.Value(1)).current;
  const quickStepDirectionRef = useRef<1 | -1>(1);
  const quickPrevStepRef = useRef<number>(1);

  const quizContext: ParsedQuizContext | undefined = useMemo(() => {
    const raw = (params as any)?.quizContext;
    const str = Array.isArray(raw) ? raw[0] : raw;
    if (str) {
      try {
        return JSON.parse(decodeURIComponent(str));
      } catch {
        try {
          return JSON.parse(str);
        } catch {
          // fallback handled below using direct query params
        }
      }
    }

    if (!isFromQuiz || !quizTrackParam) return undefined;

    let topTracks: string[] = [quizTrackParam];
    const topRaw = (params as any)?.quizTop;
    const topStr = Array.isArray(topRaw) ? topRaw[0] : topRaw;
    if (topStr) {
      try {
        const parsedTop = JSON.parse(topStr);
        if (Array.isArray(parsedTop)) {
          const safe = parsedTop.filter((k): k is string => typeof k === 'string' && k.trim().length > 0);
          if (safe.length > 0) topTracks = safe;
        }
      } catch {
        // keep fallback track only
      }
    }

    const defaultLevels = { best: 'גבוהה', second: 'בינונית', third: 'נמוכה' };
    const levelsRaw = (params as any)?.quizLevels;
    const levelsStr = Array.isArray(levelsRaw) ? levelsRaw[0] : levelsRaw;
    let levels = defaultLevels;
    if (levelsStr) {
      try {
        const parsedLevels = JSON.parse(levelsStr);
        if (parsedLevels && typeof parsedLevels === 'object') {
          levels = {
            best: (parsedLevels.best ?? defaultLevels.best).toString(),
            second: (parsedLevels.second ?? defaultLevels.second).toString(),
            third: (parsedLevels.third ?? defaultLevels.third).toString(),
          };
        }
      } catch {
        // keep defaults
      }
    }

    const leadCapturedRaw = (params as any)?.quizLeadCaptured;
    const leadCapturedStr = Array.isArray(leadCapturedRaw) ? leadCapturedRaw[0] : leadCapturedRaw;

    return {
      trackKey: quizTrackParam.toString(),
      trackTitle: (quizTrackTitleParam || quizTrackParam).toString(),
      topTracks,
      levels,
      leadCaptured: leadCapturedStr === 'true',
    };
  }, [
    (params as any)?.quizContext,
    (params as any)?.quizTop,
    (params as any)?.quizLevels,
    (params as any)?.quizLeadCaptured,
    isFromQuiz,
    quizTrackParam,
    quizTrackTitleParam,
  ]);

  const {
    state,
    dispatch,
    sendMessage,
    handleQuickReply,
    handleMentorClick,
    dismissLeadGate,
    setUserInteracted,
    loadConversationById,
    startNewConversation,
    handleMessageBlockEmailSubmit,
  } = useChat(shouldSkipWelcome, undefined, quizContext, homeStartIntro);

  const dynamicStyles = useChatStyles();

  const {
    flatListRef,
    onScroll,
    onContentSizeChange,
    showScrollToBottom,
    forceScrollToBottom,
    scrollToBottom,
  } = useChatAutoScroll({
    messageCount: state.messages.length,
    isLoading: state.isLoading,
    isSearching: state.isSearching,
    shouldAutoScroll: state.shouldAutoScroll,
    extraDeps: [state.courses.length, state.quickReplies.length, state.searchingMessage],
  });

  const [inputText, setInputText] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  const [bookCallCourse, setBookCallCourse] = useState<Course | null>(null);
  const [showMessageBlockEmailModal, setShowMessageBlockEmailModal] = useState(false);
  const [messageBlockEmail, setMessageBlockEmail] = useState('');
  const [messageBlockEmailError, setMessageBlockEmailError] = useState<string | null>(null);
  const [isSubmittingMessageBlockEmail, setIsSubmittingMessageBlockEmail] = useState(false);

  usePageEngagement('/chat');

  useEffect(() => {
    if (state.isMessageLimitReached && !showMessageBlockEmailModal) {
      setShowMessageBlockEmailModal(true);
    }
  }, [state.isMessageLimitReached, showMessageBlockEmailModal]);

  useEffect(() => {
    if (campaignRedirectDoneRef.current) return;
    if (!entryParam) return;
    if (entryParam !== 'quiz') return;
    campaignRedirectDoneRef.current = true;
    const nextParams: any = {
      ...((params as any) || {}),
      from: fromParam || 'ad',
      source: campaignSourceParam || fromParam || 'ad',
      quizSource: 'ad',
    };
    router.replace({ pathname: '/quiz', params: nextParams } as any);
  }, [entryParam, params, router, fromParam, campaignSourceParam]);

  useEffect(() => {
    void trackPageView('/chat');
    void trackFunnelStep('chat_start', {
      source: campaignSourceParam || fromParam || (isFromQuiz ? 'quiz' : 'direct'),
      campaign: campaignNameParam || undefined,
      medium: campaignMediumParam || undefined,
      content: campaignContentParam || undefined,
      term: campaignTermParam || undefined,
      topic: campaignTopicParam || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [toastVisible, setToastVisible] = useState(false);
  const [showConversationsModal, setShowConversationsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalType, setEmailModalType] = useState<'comparison' | 'recommendation'>('comparison');
  const [hasEmailBeenCaptured, setHasEmailBeenCaptured] = useState(false);
  const [areCoursesAnimationsComplete, setAreCoursesAnimationsComplete] = useState(false);

  const [hasClosedCoursesModal, setHasClosedCoursesModal] = useState(false);
  const [savedCoursesData, setSavedCoursesData] = useState<{
    courses: Course[];
    matchCount: number;
    conversationId: string | null;
    topic?: string;
    subTopic?: string;
  } | null>(null);

  const [showSavedLeadGate, setShowSavedLeadGate] = useState(false);

  const liveLeadGateData = state.leadGateData as any | null;
  const isLeadGateVisible = !!liveLeadGateData || (showSavedLeadGate && !!savedCoursesData);

  const leadGateCoursesForUi: Course[] = (liveLeadGateData?.courses ??
    (showSavedLeadGate ? savedCoursesData?.courses : []) ??
    []) as Course[];

  const leadGateMatchCountForUi: number = (liveLeadGateData?.matchCount ??
    (showSavedLeadGate ? savedCoursesData?.matchCount : 0) ??
    0) as number;

  const leadGateConversationIdForUi: string | null =
    (liveLeadGateData ? state.currentConversationId : savedCoursesData?.conversationId) ?? state.currentConversationId ?? null;

  const leadGateTopicForUi: string | null =
    (liveLeadGateData ? state.lastSearchData?.topic : savedCoursesData?.topic) ?? (state.lastSearchData?.topic ?? null);

  const leadGateSubTopicForUi: string | null =
    (liveLeadGateData ? state.lastSearchData?.subtopic : savedCoursesData?.subTopic) ?? (state.lastSearchData?.subtopic ?? null);

  useEffect(() => {
    if (!liveLeadGateData) return;
    setShowSavedLeadGate(false);
    setHasClosedCoursesModal(false);
    exitPromptShownOnceRef.current = false;
    exitPromptDismissedRef.current = false;
  }, [liveLeadGateData]);

  const handleBookCall = useCallback(
    (course: Course) => {
      void trackButtonClick('chat_book_call', '/chat', {
        course_name: course.name,
        mentor_name: course.mentor,
      });
      void trackFunnelStep('lead_modal_open', { source: 'book_call', course_name: course.name });
      setUserInteracted();
      setBookCallCourse(course);
      setShowBookCallModal(true);
    },
    [setUserInteracted],
  );

  const handleEmailSubmit = useCallback(
    async (email: string): Promise<void> => {
      const result = await courseRequestService.saveCourseRequest({
        email,
        searchData: state.lastSearchData || null,
        conversationHistory: state.messages,
        conversationId: state.currentConversationId,
      });
      if (!result.success) throw new Error(result.error || 'שגיאה בשמירת המייל');
    },
    [state.lastSearchData, state.messages, state.currentConversationId],
  );

  const handleAllAnimationsComplete = useCallback(() => {
    setAreCoursesAnimationsComplete(true);
  }, []);

  useEffect(() => {
    if (!showEmailModal && exitEmailDraftRef.current) {
      setExitEmail(exitEmailDraftRef.current);
      exitEmailDraftRef.current = '';
    }
  }, [showEmailModal]);

  useEffect(() => {
    if (
      savedCoursesData &&
      savedCoursesData.conversationId !== null &&
      state.currentConversationId !== null &&
      savedCoursesData.conversationId !== state.currentConversationId
    ) {
      setSavedCoursesData(null);
      setHasClosedCoursesModal(false);
      setShowSavedLeadGate(false);
    }
  }, [state.currentConversationId, savedCoursesData]);

  useEffect(() => {
    forceScrollToBottom();
  }, [state.currentConversationId, forceScrollToBottom]);

  const quickRepliesForBubbles = useMemo(
    () => state.quickReplies.filter((q) => q !== RESUME_LEAD_GATE_TEXT),
    [state.quickReplies],
  );

  useEffect(() => {
    if (quickRepliesForBubbles.length > 0) {
      setTimeout(() => forceScrollToBottom(), 50);
    }
  }, [quickRepliesForBubbles.length, forceScrollToBottom]);

  useEffect(() => {
    if (state.courses.length > 0) setAreCoursesAnimationsComplete(false);
  }, [state.courses]);

  const handleSendMessage = useCallback(async (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || trimmedText.length > MAX_MESSAGE_LENGTH) return;
    setUserInteracted();
    await sendMessage(trimmedText);
    forceScrollToBottom();
  }, [forceScrollToBottom, sendMessage, setUserInteracted]);

  const handleReturnToEntrySelection = useCallback(() => {
    setEntryMode(null);
    setShowWelcomeScreen(true);
    setQuickFlowError(null);
    setQuickFlowResult(null);
    quickPrevStepRef.current = 1;
  }, []);

  const handleRegularModeSelect = useCallback(() => {
    setEntryMode('regular');
    setShowWelcomeScreen(false);
    setQuickFlowError(null);
    setQuickFlowResult(null);
    void clearQuickFlowState();
    dispatch({
      type: 'SET_MESSAGES',
      payload: [
        {
          id: 'welcome-regular',
          role: 'assistant' as const,
          content: 'ספרו לי — מה הכיוון שמעניין אתכם ללמוד? אפשר בקצרה ואפשר בהרחבה, אני אתאים את החיפוש בדיוק למה שתכתבו.',
          timestamp: Date.now(),
        },
      ],
    });
  }, [dispatch]);

  const handleQuickModeSelect = useCallback(async () => {
    setEntryMode('quick');
    setShowWelcomeScreen(false);
    setQuickFlowError(null);
    setQuickFlowResult(null);
    const saved = await loadQuickFlowState();
    if (saved?.steps?.length) {
      setQuickFlow(saved);
      quickPrevStepRef.current = saved.currentStep;
    } else {
      const initial = getInitialQuickFlowSnapshot();
      setQuickFlow(initial);
      quickPrevStepRef.current = initial.currentStep;
      await persistQuickFlowState(initial);
    }
  }, []);

  const handleQuickBack = useCallback(async () => {
    if (quickFlowLoading || quickFlowSubmitting) return;
    if (quickFlow.currentStep <= 1) return;
    quickStepDirectionRef.current = -1;
    const nextStep = quickFlow.currentStep - 1;
    const nextState: QuickFlowStateSnapshot = {
      currentStep: nextStep,
      steps: quickFlow.steps.slice(0, nextStep),
      answers: quickFlow.answers.slice(0, nextStep - 1),
    };
    setQuickFlow(nextState);
    setQuickFlowError(null);
    await persistQuickFlowState(nextState);
  }, [quickFlow, quickFlowLoading, quickFlowSubmitting]);

  const animateQuickStep = useCallback(() => {
    const rtlDirectionFactor = I18nManager.isRTL ? -1 : 1;
    const startOffset = quickStepDirectionRef.current * rtlDirectionFactor * 48;

    quickStepAnim.setValue(0.985);
    quickStepOpacity.setValue(0.3);
    quickStepTranslateX.setValue(startOffset);

    Animated.parallel([
      Animated.spring(quickStepAnim, {
        toValue: 1,
        speed: 15,
        bounciness: 5,
        useNativeDriver: true,
      }),
      Animated.timing(quickStepOpacity, {
        toValue: 1,
        duration: 260,
        useNativeDriver: true,
      }),
      Animated.timing(quickStepTranslateX, {
        toValue: 0,
        duration: 320,
        useNativeDriver: true,
      }),
    ]).start();
  }, [quickStepAnim, quickStepOpacity, quickStepTranslateX]);

  const handleQuickAnswerSelect = useCallback(async (option: QuickFlowOption) => {
    if (quickFlowLoading || quickFlowSubmitting) return;
    const activeStep = quickFlow.steps[quickFlow.currentStep - 1];
    if (!activeStep) return;

    const nextAnswers = [
      ...quickFlow.answers.slice(0, quickFlow.currentStep - 1),
      {
        step: activeStep.step,
        question: activeStep.question,
        focus: activeStep.focus,
        option,
      },
    ];

    if (quickFlow.currentStep >= QUICK_FLOW_TOTAL_STEPS) {
      setQuickFlowSubmitting(true);
      setQuickFlowError(null);
      try {
        const result = await runQuickFlowRecommendation(nextAnswers);
        setQuickFlow((prev) => ({
          ...prev,
          answers: nextAnswers,
        }));
        setQuickFlowResult({
          courses: result.courses || [],
          matchCount: result.matchCount || 0,
          userSummary: result.userSummary || '',
          searchData: result.searchData,
        });
        await clearQuickFlowState();
      } catch (err) {
        console.error('[quickFlow] recommendation failed', err);
        setQuickFlowError('לא הצלחנו להביא התאמות כרגע. אפשר לנסות שוב.');
      } finally {
        setQuickFlowSubmitting(false);
      }
      return;
    }

    const nextStepNumber = quickFlow.currentStep + 1;
    quickStepDirectionRef.current = 1;
    setQuickFlowLoading(true);
    setQuickFlowError(null);
    try {
      const nextStep = await generateDynamicStep(nextStepNumber, nextAnswers);
      const nextState: QuickFlowStateSnapshot = {
        currentStep: nextStepNumber,
        answers: nextAnswers,
        steps: [...quickFlow.steps.slice(0, quickFlow.currentStep), nextStep],
      };
      setQuickFlow(nextState);
      await persistQuickFlowState(nextState);
    } finally {
      setQuickFlowLoading(false);
    }
  }, [quickFlow, quickFlowLoading, quickFlowSubmitting]);

  useEffect(() => {
    if (entryMode !== 'quick') return;
    if (quickPrevStepRef.current === quickFlow.currentStep) return;
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    animateQuickStep();
    quickPrevStepRef.current = quickFlow.currentStep;
  }, [animateQuickStep, entryMode, quickFlow.currentStep]);

  useEffect(() => {
    if (showWelcomeScreen || entryMode !== 'regular') return;
    if (initialMessageParam && !initialMessageProcessed.current) {
      initialMessageProcessed.current = true;
      handleSendMessage(initialMessageParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessageParam, showWelcomeScreen, entryMode]);

  useEffect(() => {
    if (showWelcomeScreen || entryMode !== 'regular') return;
    if (!isFromQuiz) return;
    if (quizContext) return;
    if (initialMessageParam) return;
    if (quizIntroInjectedRef.current) return;
    if (state.messages.length > 0) return;
    if (inputText.trim().length > 0) return;
    quizIntroInjectedRef.current = true;
    const trackTitle = (quizTrackTitleParam || '').trim();
    const msg = trackTitle
      ? `סיימתי את השאלון. אני רוצה ללמוד קורס בתחום "${trackTitle}" שמתאים לי. תמצא לי קורסים רלוונטיים ותשאל אותי 2–3 שאלות קצרות כדי לדייק.`
      : 'סיימתי את השאלון. אני רוצה ללמוד קורס בתחום שמתאים לי. תמצא לי קורסים רלוונטיים ותשאל אותי 2–3 שאלות קצרות כדי לדייק.';
    void handleSendMessage(msg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWelcomeScreen, entryMode, isFromQuiz, initialMessageParam, state.messages.length, inputText, quizTrackTitleParam, handleSendMessage]);

  useEffect(() => {
    if (showWelcomeScreen || entryMode !== 'regular') return;
    if (!fromHeader) return;
    if (initialMessageParam) return;
    if (headerIntroInjectedRef.current) return;
    if (state.messages.length > 0) return;
    if (inputText.trim().length > 0) return;
    headerIntroInjectedRef.current = true;
    void handleSendMessage('בוא נמצא לך קורס שמתאים בדיוק לך. מה התחום שמעניין אותך ומה המטרה שלך?');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWelcomeScreen, entryMode, fromHeader, initialMessageParam, state.messages.length, inputText, handleSendMessage]);

  const campaignIntroInjectedRef = useRef(false);
  useEffect(() => {
    const isCampaign = !!(campaignSourceParam || campaignNameParam || campaignMediumParam || campaignContentParam || campaignTermParam);
    if (!isCampaign) return;
    if (isAdTraffic) return;
    if (showWelcomeScreen || entryMode !== 'regular') return;
    if (initialMessageParam) return;
    if (isFromQuiz) return;
    if (campaignIntroInjectedRef.current) return;
    if (!shouldSkipWelcome) return;
    if (state.messages.length > 0) return;
    if (inputText.trim().length > 0) return;
    const t = (campaignTopicParam || '').toString().trim();
    const intro = t
      ? `הגעת מהמודעה על "${t}". כדי להתאים לך קורס מדויק — מה המטרה שלך ומה הרמה שלך?`
      : 'הגעת מהמודעה. כדי להתאים לך קורס מדויק — מה המטרה שלך ומה הרמה שלך?';
    campaignIntroInjectedRef.current = true;
    void handleSendMessage(intro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaignSourceParam, campaignNameParam, campaignMediumParam, campaignContentParam, campaignTermParam,
    campaignTopicParam, shouldSkipWelcome, initialMessageParam, isFromQuiz, isAdTraffic, showWelcomeScreen, entryMode,
    state.messages.length, inputText, handleSendMessage,
  ]);

  useEffect(() => {
    if (showWelcomeScreen || entryMode !== 'regular') return;
    if ((params as any)?.conversationId) {
      const raw = (params as any)?.conversationId;
      const conversationId = Array.isArray(raw) ? raw[0] : raw;
      if (conversationId) loadConversationById(conversationId);
    }
  }, [showWelcomeScreen, entryMode, (params as any)?.conversationId, loadConversationById]);

  const leadGateHasMatches = useMemo(() => {
    if (!isLeadGateVisible) return false;
    return (leadGateMatchCountForUi ?? 0) > 0 || (leadGateCoursesForUi ?? []).length > 0;
  }, [isLeadGateVisible, leadGateMatchCountForUi, leadGateCoursesForUi]);

  const leadGateTopCoursesForSummary = useMemo(() => {
    const courses = (leadGateCoursesForUi || []) as any[];
    const n = Math.min(3, Math.max(0, leadGateMatchCountForUi || courses.length || 0));
    return courses.slice(0, n);
  }, [leadGateCoursesForUi, leadGateMatchCountForUi]);

  const shouldOfferExitSummaryB = useMemo(() => {
    if (!leadGateHasMatches) return false;
    if (exitSummarySentRef.current) return false;
    if (hasEmailBeenCaptured) return false;
    if (exitPromptDismissedRef.current) return false;
    if (showExitSummaryPrompt || showExitEmailModal || showEmailModal) return false;
    return true;
  }, [leadGateHasMatches, hasEmailBeenCaptured, showExitSummaryPrompt, showExitEmailModal, showEmailModal]);

  const performExit = useCallback(
    (exit: PendingExit | null) => {
      if (!exit) return;
      if (exit.kind === 'closeLeadGate') {
        setShowSavedLeadGate(false);
        dismissLeadGate();
        return;
      }
      if (exit.kind === 'home') {
        router.push('/');
        return;
      }
      if (exit.kind === 'goBack') {
        if (exit.action) {
          allowNextGoBackRef.current = true;
          (navigation as any).dispatch(exit.action);
          return;
        }
        router.back();
      }
    },
    [dismissLeadGate, navigation, router],
  );

  const requestExit = useCallback(
    (exit: PendingExit) => {
      if (shouldOfferExitSummaryB && !exitPromptShownOnceRef.current) {
        exitPromptShownOnceRef.current = true;
        setPendingExit(exit);
        setShowExitSummaryPrompt(true);
        return;
      }
      performExit(exit);
    },
    [performExit, shouldOfferExitSummaryB],
  );

  useEffect(() => {
    const unsub = (navigation as any).addListener('beforeRemove', (e: any) => {
      const actionType = e?.data?.action?.type;
      if (showExitEmailModal) {
        e.preventDefault();
        setShowExitEmailModal(false);
        setExitEmailError(null);
        setExitSubmitting(false);
        return;
      }
      if (showExitSummaryPrompt) {
        e.preventDefault();
        setShowExitSummaryPrompt(false);
        setPendingExit(null);
        return;
      }
      if (allowNextGoBackRef.current) {
        allowNextGoBackRef.current = false;
        return;
      }
      if (actionType !== 'GO_BACK') return;
      if (!leadGateHasMatches) return;
      if (shouldOfferExitSummaryB) {
        e.preventDefault();
        requestExit({ kind: 'goBack', action: e.data.action });
      } else {
        e.preventDefault();
        requestExit({ kind: 'closeLeadGate' });
      }
    });
    return unsub;
  }, [navigation, leadGateHasMatches, shouldOfferExitSummaryB, requestExit, showExitSummaryPrompt, showExitEmailModal]);

  const flatListData = useMemo(() => {
    const data: MessageItem[] = [];
    let coursesAdded = false;

    state.messages.forEach((msg) => {
      if (msg.content === '[LEAD_GATE]') return;
      data.push({ type: 'message', data: msg });
      if (state.coursesMessageId && msg.id === state.coursesMessageId && state.courses.length > 0) {
        data.push({ type: 'courses', data: state.courses });
        coursesAdded = true;
        if (!hasEmailBeenCaptured && areCoursesAnimationsComplete) {
          data.push({ type: 'emailButton' });
        }
      }
    });

    if (state.courses.length > 0 && !coursesAdded) {
      data.push({ type: 'courses', data: state.courses });
      if (!hasEmailBeenCaptured && areCoursesAnimationsComplete) {
        data.push({ type: 'emailButton' });
      }
    }

    if (state.isLoading && !state.isSearching) {
      data.push({ type: 'typing' });
    }

    return data;
  }, [
    state.messages,
    state.courses,
    state.coursesMessageId,
    state.isLoading,
    state.isSearching,
    hasEmailBeenCaptured,
    areCoursesAnimationsComplete,
  ]);

  const handleSend = () => {
    if (state.isMessageLimitReached) {
      setShowMessageBlockEmailModal(true);
      return;
    }
    const text = inputText.trim();
    if (text && !state.isLoading && !state.isLeadGateLocked) {
      handleSendMessage(text);
      setInputText('');
    }
  };

  const handleSubmitMessageBlockEmail = async () => {
    const email = messageBlockEmail.trim();

    if (!isValidEmail(email)) {
      setMessageBlockEmailError('נא להזין כתובת מייל תקינה');
      return;
    }

    setIsSubmittingMessageBlockEmail(true);
    setMessageBlockEmailError(null);

    try {
      await handleMessageBlockEmailSubmit(email);
      setShowMessageBlockEmailModal(false);
      setMessageBlockEmail('');
    } catch (err) {
      console.error('[handleSubmitMessageBlockEmail] Error:', err);
      setMessageBlockEmailError('אירעה שגיאה. נסה שוב.');
    } finally {
      setIsSubmittingMessageBlockEmail(false);
    }
  };

  const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (Platform.OS === 'web') {
      const nativeEvent = e.nativeEvent as any;
      if (nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const handleShowConversations = useCallback(() => {
    void trackButtonClick('chat_history_open', '/chat');
    setShowConversationsModal(true);
  }, []);

  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      void trackButtonClick('chat_conversation_select', '/chat', { conversation_id: conversationId });
      await loadConversationById(conversationId);
    },
    [loadConversationById],
  );

  const handleNewConversation = useCallback(async () => {
    void trackButtonClick('chat_new_conversation', '/chat');
    await startNewConversation();
  }, [startNewConversation]);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      router.replace('/');
    } catch {}
  }, [router]);

  const handleLeadSubmit = async (leadData: LeadData) => {
    const result = await leadSubmissionService.submitLead({
      ...leadData,
      source: 'website',
      submittedAt: new Date().toISOString(),
      messages: state.messages,
    });
    if (!result.success) throw new Error(result.error || 'Lead submit failed');
  };

  const handleExitEmailSubmit = useCallback(async () => {
    const email = normalizeEmail(exitEmail);
    setExitEmailError(null);
    if (!isValidEmail(email)) {
      setExitEmailError('אנא הזן כתובת מייל תקינה');
      return;
    }
    if (!exitConsent) {
      setExitEmailError('יש לאשר קבלת סיכום למייל');
      return;
    }
    const hasAnyLeadData = !!(state.leadGateData as any | null) || !!savedCoursesData;
    if (!hasAnyLeadData) {
      setExitEmailError('משהו השתבש. נסה שוב.');
      return;
    }
    setExitSubmitting(true);
    try {
      const selected_courses = leadGateTopCoursesForSummary.map((c: any) => ({
        id: c.id,
        name: (c.marketing_title || c.name || '').toString(),
      }));
      const baseSearchData = ((state.leadGateData as any | null)?.searchData || {}) as any;
      const fallbackSearchData =
        (state.leadGateData ? {} : { topic: leadGateTopicForUi, subtopic: leadGateSubTopicForUi }) as any;
      const result = await courseRequestService.saveCourseRequest({
        email,
        searchData: {
          ...fallbackSearchData,
          ...baseSearchData,
          selected_courses,
          source: 'exit_summary',
          campaign: campaignNameParam || undefined,
          utm_source: campaignSourceParam || undefined,
          utm_medium: campaignMediumParam || undefined,
          utm_content: campaignContentParam || undefined,
          utm_term: campaignTermParam || undefined,
        },
        conversationHistory: state.messages,
        conversationId: leadGateConversationIdForUi,
      });
      if (!result.success) throw new Error(result.error || 'אירעה שגיאה. נסה שוב.');
      exitSummarySentRef.current = true;
      setHasEmailBeenCaptured(true);
      setShowExitEmailModal(false);
      setShowExitSummaryPrompt(false);
      const exit = pendingExit;
      setPendingExit(null);
      setToastVisible(true);
      setTimeout(() => performExit(exit), 300);
    } catch (err: any) {
      setExitEmailError(err?.message || 'אירעה שגיאה. נסה שוב.');
    } finally {
      setExitSubmitting(false);
    }
  }, [
    exitEmail, exitConsent, pendingExit, performExit,
    state.leadGateData, savedCoursesData, state.messages,
    leadGateConversationIdForUi, leadGateTopCoursesForSummary,
    leadGateTopicForUi, leadGateSubTopicForUi,
    campaignNameParam, campaignSourceParam, campaignMediumParam, campaignContentParam, campaignTermParam,
  ]);

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<MessageItem>) => {
      switch (item.type) {
        case 'message':
          return (
            <LazyChatMessage
              key={item.data.id}
              message={item.data}
              onBookCall={handleBookCall}
              onMentorClick={handleMentorClick}
              skipTyping={state.skipTypingIds.has(item.data.id)}
              displayedCourses={state.lastDisplayedCourses}
              recommendedCourse={state.recommendedCourse || undefined}
              onEmailSubmit={handleEmailSubmit}
            />
          );
        case 'courses':
          return (
            <LazyCoursesGrid
              key="courses-grid"
              courses={item.data}
              onBookCall={handleBookCall}
              onMentorClick={handleMentorClick}
              onAllAnimationsComplete={handleAllAnimationsComplete}
            />
          );
        case 'emailButton':
          return (
            <View key="email-button" style={styles.emailButtonContainer}>
              <Pressable
                style={({ pressed }) => [styles.emailButton, pressed && styles.emailButtonPressed]}
                onPress={() => {
                  void trackButtonClick('chat_email_capture_open', '/chat', { type: 'comparison' });
                  setEmailModalType('comparison');
                  setShowEmailModal(true);
                }}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="קבל סיכום ותוצאות במייל"
              >
                <Mail size={20} color="#FAF5EA" strokeWidth={2} />
                <Text style={styles.emailButtonText}>קבלו סיכום קורסים למייל</Text>
              </Pressable>
            </View>
          );
        case 'typing':
          return <TypingIndicator key="typing-indicator" />;
        default:
          return null;
      }
    },
    [
      handleBookCall, handleMentorClick, handleEmailSubmit, handleAllAnimationsComplete,
      state.skipTypingIds, state.lastDisplayedCourses, state.recommendedCourse,
      state.currentConversationId, state.messages,
    ],
  );

  const keyExtractor = (item: MessageItem, index: number) => {
    switch (item.type) {
      case 'message': return `message-${item.data.id}`;
      case 'courses': return 'courses-grid';
      case 'emailButton': return 'email-button';
      case 'typing': return 'typing-indicator';
      default: return `item-${index}`;
    }
  };

  const shouldShowResumeLeadGateCTA = useMemo(
    () => !isLeadGateVisible && state.quickReplies.includes(RESUME_LEAD_GATE_TEXT) && !state.isLeadGateLocked,
    [isLeadGateVisible, state.quickReplies, state.isLeadGateLocked],
  );

  const inputCharCount = inputText.length;
  const inputAtLimit = inputCharCount >= MAX_MESSAGE_LENGTH;

  const showProgress = !showWelcomeScreen && entryMode !== 'quick' && state.messages.length > 0 && !liveLeadGateData;
  const progressStep = state.isSearching ? 3 : state.userMessageCount > 1 ? 2 : 1;
  const progressLabel = [
    'מבינים מה אתה צריך',
    'מדייקים את החיפוש',
    'מוצאים לך קורסים',
  ][progressStep - 1];
  const isQuickExperience = !showWelcomeScreen && entryMode === 'quick';

  return (
    <View style={styles.screen}>
      <Head>
        <title>צ&#39;אט חכם להתאמת קורסים | Kursi</title>
        <meta name="description" content="דברו עם המערכת החכמה שלנו וקבלו המלצות מותאמות אישית לקורסים בתחום שלכם. התאמה אישית מבוססת AI" />
        <meta property="og:title" content="צ&#39;אט חכם להתאמת קורסים | Kursi" />
        <meta property="og:description" content="דברו עם המערכת החכמה שלנו וקבלו המלצות מותאמות אישית" />
        <meta property="og:image" content="https://kursiaii.com/og-image.webp" />
        <meta property="og:url" content="https://kursiaii.com/chat" />
        <link rel="canonical" href="https://kursiaii.com/chat" />
        <meta name="robots" content="noindex, nofollow" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
        />
      </Head>

      <Text
        style={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
        {...(Platform.OS === 'web' && ({ role: 'heading', 'aria-level': 1 } as any))}
      >
        צ&#39;אט עם Kursi - עוזר בחירת קורסים
      </Text>

      <KeyboardAvoidingView
        style={[styles.container, dynamicStyles.container]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        {!isQuickExperience && (
          <View style={styles.header}>
            <View style={styles.headerUserArea}>
              <ChatHeader onShowConversations={handleShowConversations} onSignOut={handleSignOut} />
            </View>

            <Pressable
              onPress={() => (leadGateHasMatches ? requestExit({ kind: 'home' }) : router.push('/'))}
              style={styles.logoButton}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel="חזרה לדף הבית"
              {...(Platform.OS === 'web' && ({ 'aria-label': 'חזרה לדף הבית' } as any))}
            >
              <Image
                source={require('@/app/assets/images/kursi-logo-new.png')}
                style={styles.logoImage}
                resizeMode="contain"
                accessible={true}
                accessibilityLabel="לוגו Kursi"
              />
            </Pressable>
          </View>
        )}

        <View style={styles.messagesWrapper}>
          {!isQuickExperience && <LazyCodeRainBackground isActive={state.isLoading} />}
          {showProgress && (
            <ProgressIndicator
              currentStep={progressStep}
              totalSteps={3}
              label={progressLabel}
            />
          )}
          {showWelcomeScreen ? (
            <ScrollView
              style={styles.welcomeScroll}
              contentContainerStyle={styles.welcomeScrollContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              bounces={true}
            >
              <View style={styles.welcomeContainer}>
                <Text style={styles.welcomeTitle}>איך נתחיל להתאים לך קורס?</Text>
                <Text style={styles.welcomeSubtitle}>
                  אפשר לדבר חופשי עם הסוכן, או להשלים מסלול מהיר של 4 צעדים בלי הקלדה.
                </Text>

                <View style={styles.entryChoiceGroup}>
                  <Pressable
                    style={({ pressed }) => [styles.entryChoiceCard, pressed && styles.entryChoiceCardPressed]}
                    onPress={handleRegularModeSelect}
                    accessibilityRole="button"
                    accessibilityLabel="בחירה בשיחה רגילה"
                  >
                    <Text style={styles.entryChoiceTitle}>שיחה רגילה</Text>
                    <Text style={styles.entryChoiceText}>למי שרוצה לתאר חופשי מה מעניין אותו ולקבל דיוק בשיחה.</Text>
                  </Pressable>

                  <Pressable
                    style={({ pressed }) => [styles.entryChoiceCard, styles.entryChoiceCardPrimary, pressed && styles.entryChoiceCardPressed]}
                    onPress={handleQuickModeSelect}
                    accessibilityRole="button"
                    accessibilityLabel="בחירה בשיחה מהירה"
                  >
                    <Text style={[styles.entryChoiceTitle, styles.entryChoiceTitlePrimary]}>שיחה מהירה</Text>
                    <Text style={[styles.entryChoiceText, styles.entryChoiceTextPrimary]}>
                      4 צעדים קצרים, רק לחיצות על תשובות, והתאמות קורסים תוך פחות מדקה.
                    </Text>
                  </Pressable>
                </View>
              </View>
            </ScrollView>
          ) : (
            entryMode === 'quick' ? (
            <View style={styles.quickFlowContainer}>
              <View style={styles.quickFlowShell}>
                <View style={styles.quickFlowStepIndicatorRow}>
                  {Array.from({ length: QUICK_FLOW_TOTAL_STEPS }).map((_, idx) => {
                    const stepNumber = idx + 1;
                    const status = quickFlow.currentStep === stepNumber ? 'active' : quickFlow.currentStep > stepNumber ? 'complete' : 'future';
                    return (
                      <View key={`quick-step-${stepNumber}`} style={styles.quickFlowStepIndicatorItem}>
                        <View style={[
                          styles.quickFlowStepIndicatorCircle,
                          status === 'active' && styles.quickFlowStepIndicatorCircleActive,
                          status === 'complete' && styles.quickFlowStepIndicatorCircleComplete,
                        ]}>
                          <Text style={[
                            styles.quickFlowStepIndicatorText,
                            status === 'active' && styles.quickFlowStepIndicatorTextActive,
                            status === 'complete' && styles.quickFlowStepIndicatorTextComplete,
                          ]}>
                            {status === 'complete' ? '✓' : stepNumber}
                          </Text>
                        </View>
                        {stepNumber < QUICK_FLOW_TOTAL_STEPS && (
                          <View style={styles.quickFlowStepConnector}>
                            <Animated.View
                              style={[
                                styles.quickFlowStepConnectorFill,
                                status === 'complete' && styles.quickFlowStepConnectorFillActive,
                              ]}
                            />
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>

                <Animated.View
                  style={[
                    styles.quickQuestionCard,
                    {
                      opacity: quickStepOpacity,
                      transform: [{ translateX: quickStepTranslateX }, { scale: quickStepAnim }],
                    },
                  ]}
                >
                  <View style={styles.quickFlowQuestionHeader}>
                    <Text style={styles.quickFlowProgressText}>שלב {quickFlow.currentStep} מתוך {QUICK_FLOW_TOTAL_STEPS}</Text>
                    <Text style={styles.quickQuestionTitle}>
                      {quickFlow.steps[quickFlow.currentStep - 1]?.question || 'אנחנו מכינים עבורך את השאלה הבאה…'}
                    </Text>
                  </View>

                  <View style={styles.quickOptionsWrap}>
                    {(quickFlow.steps[quickFlow.currentStep - 1]?.options || []).map((option) => {
                      const selected = quickFlow.answers[quickFlow.currentStep - 1]?.option?.id === option.id;
                      return (
                        <Pressable
                          key={`${quickFlow.currentStep}-${option.id}`}
                          style={({ pressed }) => [
                            styles.quickOptionCard,
                            selected && styles.quickOptionCardSelected,
                            pressed && styles.quickOptionCardPressed,
                          ]}
                          onPress={() => handleQuickAnswerSelect(option)}
                          disabled={quickFlowLoading || quickFlowSubmitting}
                          accessibilityRole="button"
                          accessibilityLabel={`בחירה: ${option.label}`}
                        >
                          <Text style={[styles.quickOptionText, selected && styles.quickOptionTextSelected]}>{option.label}</Text>
                        </Pressable>
                      );
                    })}
                  </View>

                  {quickFlow.currentStep > 1 && (
                    <View style={styles.quickFlowFooter}>
                      <Pressable
                        style={({ pressed }) => [styles.quickFlowBackButton, pressed && styles.quickFlowBackButtonPressed]}
                        onPress={handleQuickBack}
                        accessibilityRole="button"
                        accessibilityLabel="חזור לשלב קודם"
                      >
                        <Text style={styles.quickFlowBackButtonText}>חזרה</Text>
                      </Pressable>
                    </View>
                  )}
                </Animated.View>
              </View>

              {(quickFlowLoading || quickFlowSubmitting) && (
                <View style={styles.quickFlowLoadingRow}>
                  <ActivityIndicator color={theme.colors.primary} size="small" />
                  <Text style={styles.quickFlowLoadingText}>
                    {quickFlowSubmitting ? 'מאתרים קורסים מדויקים לפי הבחירות שלך…' : 'מחדדים עבורך את השאלה הבאה…'}
                  </Text>
                </View>
              )}

              {!!quickFlowError && <Text style={styles.quickFlowErrorText}>{quickFlowError}</Text>}

              {!!quickFlowResult && !quickFlowSubmitting && (
                <View style={styles.quickFlowResultsBlock}>
                  <Text style={styles.quickFlowResultsTitle}>מצאנוּ {quickFlowResult.matchCount} התאמות</Text>
                  <Text style={styles.quickFlowResultsSubtitle}>
                    {quickFlowResult.userSummary || 'בחר קורס שמרגיש לך הכי נכון ונתקדם יחד.'}
                  </Text>
                  <Pressable
                    style={({ pressed }) => [styles.quickFlowResultsButton, pressed && styles.quickOptionCardPressed]}
                    onPress={() => {
                      setSavedCoursesData({
                        courses: quickFlowResult.courses || [],
                        matchCount: quickFlowResult.matchCount || 0,
                        conversationId: state.currentConversationId || null,
                        topic: quickFlowResult.searchData?.topic,
                        subTopic: quickFlowResult.searchData?.subtopic,
                      });
                      setShowSavedLeadGate(true);
                    }}
                  >
                    <Text style={styles.quickFlowResultsButtonText}>צפייה בקורסים המומלצים</Text>
                  </Pressable>
                </View>
              )}
            </View>
            ) : (
            <>
              <FlatList
                ref={flatListRef}
                data={flatListData}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                extraData={{
                  messagesLength: state.messages.length,
                  coursesLength: state.courses.length,
                  coursesMessageId: state.coursesMessageId,
                  recommendedCourse: state.recommendedCourse,
                  isLoading: state.isLoading,
                  lastDisplayedCourses: state.lastDisplayedCourses,
                  quickReplies: state.quickReplies,
                  skipTypingIds: state.skipTypingIds,
                  areCoursesAnimationsComplete,
                  hasEmailBeenCaptured,
                }}
                contentContainerStyle={styles.messagesContent}
                showsVerticalScrollIndicator={true}
                persistentScrollbar={true}
                keyboardShouldPersistTaps="handled"
                style={[
                  styles.messagesList,
                  Platform.OS === 'web'
                    ? ({ scrollbarWidth: 'thin', scrollbarColor: `${theme.colors.primary}40 transparent` } as any)
                    : null,
                ]}
                onScroll={onScroll}
                scrollEventThrottle={16}
                onContentSizeChange={onContentSizeChange}
                removeClippedSubviews={Platform.OS !== 'web'}
                maxToRenderPerBatch={10}
                updateCellsBatchingPeriod={50}
                windowSize={10}
                initialNumToRender={15}
              />
              {showScrollToBottom && (
                <View style={[styles.scrollToBottomWrap, { pointerEvents: 'box-none' }]}>
                  <Pressable
                    style={({ pressed }) => [styles.scrollToBottomBtn, pressed && styles.scrollToBottomBtnPressed]}
                    onPress={() => {
                      void trackButtonClick('chat_scroll_bottom', '/chat');
                      scrollToBottom(true);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="גלול לתחתית הצ'אט"
                  >
                    <Text style={styles.scrollToBottomText}>↓</Text>
                  </Pressable>
                </View>
              )}
            </>
            )
          )}
        </View>

        {!showWelcomeScreen && entryMode !== 'quick' && quickRepliesForBubbles.length > 0 && (
          <QuickReplies options={quickRepliesForBubbles} onSelect={handleQuickReply} />
        )}

        {!showWelcomeScreen && entryMode !== 'quick' && savedCoursesData && !isLeadGateVisible && (
          <View style={styles.resumeLeadGateContainer}>
            <View style={styles.resumeLeadGateInner}>
              <Pressable
                style={({ pressed }) => [styles.viewCoursesAgainButton, pressed && styles.viewCoursesAgainButtonPressed]}
                onPress={() => setShowSavedLeadGate(true)}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={hasClosedCoursesModal ? 'צפה שוב בקורסים' : 'צפה בקורסים שמצאתי'}
              >
                <Text style={styles.viewCoursesAgainText}>
                  {hasClosedCoursesModal ? 'צפו שוב בקורסים' : 'הקורסים שמצאתי'}
                </Text>
              </Pressable>
            </View>
          </View>
        )}

        {!showWelcomeScreen && entryMode !== 'quick' && shouldShowResumeLeadGateCTA && (
          <View style={styles.resumeLeadGateContainer}>
            <View style={styles.resumeLeadGateInner}>
              <Pressable
                style={({ pressed }) => [styles.resumeLeadGateButton, pressed && styles.resumeLeadGateButtonPressed]}
                onPress={() => handleSendMessage(RESUME_LEAD_GATE_TEXT)}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="המשך השארת פרטים"
              >
                <Text style={styles.resumeLeadGateText}>המשך השארת פרטים</Text>
              </Pressable>
            </View>
          </View>
        )}

        {!showWelcomeScreen && entryMode !== 'quick' && (
          <View style={[styles.inputWrapper, dynamicStyles.inputContainer]}>
            <View style={styles.inputContainer}>
              <Pressable
                style={({ pressed, focused }) => [
                  styles.sendButton,
                  dynamicStyles.sendButton,
                  pressed && styles.sendButtonPressed,
                  (!inputText.trim() || state.isLeadGateLocked) && styles.sendButtonDisabled,
                  settings.keyboardNavigation && focused && styles.sendButtonFocused,
                ]}
                onPress={handleSend}
                disabled={!inputText.trim() || state.isLoading || state.isLeadGateLocked}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={state.isLoading ? 'שולח הודעה' : 'שלח הודעה'}
                accessibilityHint="לחץ כדי לשלוח את ההודעה"
                accessibilityState={{ disabled: !inputText.trim() || state.isLoading || state.isLeadGateLocked }}
                {...(Platform.OS === 'web' && ({ 'aria-label': 'שלח הודעה' } as any))}
              >
                <View aria-hidden={true}>
                  <Send
                    color={inputText.trim() && !state.isLeadGateLocked ? '#FAF5EA' : theme.colors.textLight}
                    size={24}
                  />
                </View>
              </Pressable>

              <View style={styles.inputFieldWrapper}>
                <TextInput
                  style={[
                    styles.input,
                    dynamicStyles.input,
                    state.isLeadGateLocked && styles.inputDisabled,
                    inputAtLimit && styles.inputAtLimit,
                  ]}
                  value={inputText}
                  onChangeText={setInputText}
                  placeholder={state.isLeadGateLocked ? 'השלב הנוכחי פתוח. סגור אותו כדי להמשיך בצ׳אט.' : state.placeholderText}
                  placeholderTextColor={theme.colors.textLight}
                  multiline
                  maxLength={MAX_MESSAGE_LENGTH}
                  editable={!state.isLoading && !state.isLeadGateLocked}
                  onSubmitEditing={Platform.OS !== 'web' ? handleSend : undefined}
                  onKeyPress={handleKeyPress}
                  blurOnSubmit={false}
                  autoFocus={false}
                  accessible={true}
                  accessibilityLabel="הקלד הודעה"
                  accessibilityHint={
                    state.isLeadGateLocked
                      ? 'השלב הנוכחי פתוח. סגור אותו כדי להמשיך בצ׳אט.'
                      : 'כתוב את השאלה שלך. Enter לשליחה, Shift+Enter לשורה חדשה'
                  }
                />
                {inputCharCount > MAX_MESSAGE_LENGTH * 0.8 && (
                  <Text style={[styles.charCounter, inputAtLimit && styles.charCounterAtLimit]}>
                    {inputCharCount}/{MAX_MESSAGE_LENGTH}
                  </Text>
                )}
              </View>
            </View>
          </View>
        )}

        {!isQuickExperience && (
          <View style={styles.disclaimerWrapper}>
            <View style={styles.disclaimerContainer}>
              <Text style={styles.disclaimerText}>קורסי עלול לטעות. חשוב לוודא מידע קריטי.</Text>
              <View style={styles.legalLinks}>
                <Pressable onPress={() => router.push('/legal/accessibility')} accessible={true} accessibilityRole="link" accessibilityLabel="הצהרת נגישות">
                  <Text style={styles.legalLinkText}>הצהרת נגישות</Text>
                </Pressable>
                <Text style={styles.legalSeparator}>•</Text>
                <Pressable onPress={() => router.push('/legal/terms')} accessible={true} accessibilityRole="link" accessibilityLabel="תנאי שימוש">
                  <Text style={styles.legalLinkText}>תנאי שימוש</Text>
                </Pressable>
                <Text style={styles.legalSeparator}>•</Text>
                <Pressable onPress={() => router.push('/legal/privacy')} accessible={true} accessibilityRole="link" accessibilityLabel="מדיניות פרטיות">
                  <Text style={styles.legalLinkText}>מדיניות פרטיות</Text>
                </Pressable>
              </View>
            </View>
          </View>
        )}

        <AccessibilityButton />

        <LazyLeadModal
          visible={showLeadModal}
          course={selectedCourse}
          onClose={() => { setShowLeadModal(false); setSelectedCourse(null); }}
          onSubmit={handleLeadSubmit}
        />

        <LazyBookCallModal
          visible={showBookCallModal}
          course={bookCallCourse}
          onClose={() => { setShowBookCallModal(false); setBookCallCourse(null); }}
        />

        <Toast
          visible={toastVisible}
          message="תודה! נשלח לך סיכום למייל בקרוב"
          type="success"
          onHide={() => setToastVisible(false)}
        />

        <ConversationsListModal
          visible={showConversationsModal}
          onClose={() => setShowConversationsModal(false)}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
        />

        <EmailCaptureModal
          visible={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          type={emailModalType}
          mainTopic={state.lastSearchData?.topic}
          subTopic={state.lastSearchData?.subtopic ?? undefined}
          coursesShown={state.courses.map((c) => c.id || c.name)}
          conversationId={state.currentConversationId}
          conversationHistory={state.messages}
          onSuccess={() => { setHasEmailBeenCaptured(true); setToastVisible(true); }}
        />

        <Modal
          visible={showMessageBlockEmailModal}
          transparent
          animationType="fade"
          onRequestClose={() => {}}
        >
          <View style={styles.messageBlockModalOverlay}>
            <View style={styles.messageBlockModalContent}>
              <Text style={styles.messageBlockModalTitle}>נגמרו ההודעות החינמיות</Text>
              <Text style={styles.messageBlockModalText}>
                השאירו מייל ותוכלו להמשיך לשוחח בלי הגבלה.
              </Text>
              <TextInput
                style={styles.messageBlockModalInput}
                value={messageBlockEmail}
                onChangeText={(text) => {
                  setMessageBlockEmail(text);
                  setMessageBlockEmailError(null);
                }}
                placeholder="כתבו את המייל שלכם"
                placeholderTextColor="#9CA3AF"
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                editable={!isSubmittingMessageBlockEmail}
                {...(Platform.OS === 'web' ? ({ dir: 'rtl', style: [styles.messageBlockModalInput, { outlineStyle: 'none' }] } as any) : {})}
              />
              {messageBlockEmailError && (
                <Text style={styles.messageBlockModalError}>{messageBlockEmailError}</Text>
              )}
              <Pressable
                style={[
                  styles.messageBlockModalButton,
                  (!isValidEmail(messageBlockEmail) || isSubmittingMessageBlockEmail) &&
                    styles.messageBlockModalButtonDisabled,
                ]}
                onPress={handleSubmitMessageBlockEmail}
                disabled={!isValidEmail(messageBlockEmail) || isSubmittingMessageBlockEmail}
              >
                {isSubmittingMessageBlockEmail ? (
                  <ActivityIndicator color="#FFF" size="small" />
                ) : (
                  <Text style={styles.messageBlockModalButtonText}>המשך</Text>
                )}
              </Pressable>
            </View>
          </View>
        </Modal>

        {state.isSearching && (
          <View
            style={styles.loadingBanner}
            accessible={true}
            accessibilityRole="progressbar"
            {...(Platform.OS === 'web' && ({ 'aria-live': 'polite' } as any))}
          >
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Text style={styles.loadingBannerText}>מחפש עבורך קורסים רלוונטיים...</Text>
          </View>
        )}
      </KeyboardAvoidingView>

      {(!!liveLeadGateData || !!savedCoursesData) && (
        <SimpleCoursesModal
          visible={isLeadGateVisible}
          courses={leadGateCoursesForUi}
          matchCount={leadGateMatchCountForUi}
          conversationId={leadGateConversationIdForUi}
          topic={leadGateTopicForUi}
          subTopic={leadGateSubTopicForUi}
          onClose={() => {
            setHasClosedCoursesModal(true);
            if (liveLeadGateData) {
              setSavedCoursesData({
                courses: (liveLeadGateData?.courses || []) as Course[],
                matchCount: (liveLeadGateData?.matchCount || 0) as number,
                conversationId: state.currentConversationId,
                topic: state.lastSearchData?.topic,
                subTopic: state.lastSearchData?.subtopic,
              });
              setShowSavedLeadGate(false);
              dismissLeadGate();
              return;
            }
            setShowSavedLeadGate(false);
          }}
        />
      )}

      <Modal
        visible={showExitSummaryPrompt}
        transparent
        animationType="fade"
        statusBarTranslucent
        presentationStyle="overFullScreen"
        onRequestClose={() => { setShowExitSummaryPrompt(false); setPendingExit(null); }}
      >
        <View style={styles.exitOverlay}>
          <Pressable style={styles.exitOverlayTouchable} onPress={() => { setShowExitSummaryPrompt(false); setPendingExit(null); }} accessible={false} />
          <View style={styles.exitCard}>
            <View style={styles.exitHeader}>
              <Pressable onPress={() => { setShowExitSummaryPrompt(false); setPendingExit(null); }} hitSlop={8} accessibilityRole="button" accessibilityLabel="סגור">
                <X size={22} color={theme.colors.textLight} strokeWidth={2} />
              </Pressable>
            </View>
            <Text style={styles.exitTitle}>רגע לפני שיוצאים — לשמור לך את ההתאמות?</Text>
            <Text style={styles.exitSubtitle}>נשלח למייל סיכום מסודר עם הקורסים הכי מתאימים כדי שלא תאבדו אותם.</Text>
            <View style={styles.exitButtons}>
              <Pressable
                style={({ pressed }) => [styles.exitPrimaryButtonRow, pressed && styles.exitPrimaryButtonPressed]}
                onPress={() => { setShowExitSummaryPrompt(false); setShowExitEmailModal(true); }}
                accessibilityRole="button"
                accessibilityLabel="פתח טופס שליחת סיכום למייל"
              >
                <Mail size={18} color="#FAF5EA" strokeWidth={2} />
                <Text style={styles.exitPrimaryButtonText}>כן, שמרו לי את הסיכום</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [styles.exitSecondaryButton, pressed && styles.exitSecondaryButtonPressed]}
                onPress={() => {
                  exitPromptDismissedRef.current = true;
                  setShowExitSummaryPrompt(false);
                  const exit = pendingExit;
                  setPendingExit(null);
                  performExit(exit);
                }}
                accessibilityRole="button"
                accessibilityLabel="המשך יציאה ללא שליחת סיכום"
              >
                <Text style={styles.exitSecondaryButtonText}>לא עכשיו, המשך יציאה</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showExitEmailModal}
        transparent
        animationType="fade"
        statusBarTranslucent
        presentationStyle="overFullScreen"
        onRequestClose={() => { setShowExitEmailModal(false); setExitEmailError(null); setExitSubmitting(false); }}
      >
        <View style={styles.exitOverlay}>
          <Pressable style={styles.exitOverlayTouchable} onPress={() => { setShowExitEmailModal(false); setExitEmailError(null); setExitSubmitting(false); }} accessible={false} />
          <View style={styles.exitCard}>
            <View style={styles.exitHeader}>
              <Pressable onPress={() => { setShowExitEmailModal(false); setExitEmailError(null); setExitSubmitting(false); }} hitSlop={8} accessibilityRole="button" accessibilityLabel="סגור">
                <X size={22} color={theme.colors.textLight} strokeWidth={2} />
              </Pressable>
            </View>
            <Text style={styles.exitTitle}>לאיזה מייל לשלוח את ההתאמות שלך?</Text>
            <Text style={styles.exitSubtitle}>נשלח תוך רגע סיכום קצר וברור שאפשר לחזור אליו בכל זמן.</Text>
            <TextInput
              style={[styles.exitInput, exitEmailError && styles.exitInputError]}
              value={exitEmail}
              onChangeText={(t) => { setExitEmail(t); setExitEmailError(null); }}
              placeholder="הכנס כתובת מייל"
              placeholderTextColor={theme.colors.textLight}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!exitSubmitting}
              accessible
              accessibilityLabel="שדה הזנת כתובת מייל"
            />
            <Pressable
              style={styles.exitCheckboxContainer}
              onPress={() => { setExitConsent(!exitConsent); setExitEmailError(null); }}
              accessible
              accessibilityRole="checkbox"
              accessibilityState={{ checked: exitConsent }}
            >
              <View style={[styles.exitCheckbox, exitConsent && styles.exitCheckboxChecked]}>
                {exitConsent && <Text style={styles.exitCheckmark}>✓</Text>}
              </View>
              <Text style={styles.exitCheckboxLabel}>אני מאשר/ת קבלת סיכום ועדכונים רלוונטיים למייל</Text>
            </Pressable>
            {exitEmailError && (
              <Text style={styles.exitErrorText} accessibilityRole="alert">{exitEmailError}</Text>
            )}
            <Pressable
              style={({ pressed }) => [styles.exitSubmitButton, pressed && styles.exitPrimaryButtonPressed, exitSubmitting && styles.exitSubmitButtonDisabled]}
              onPress={handleExitEmailSubmit}
              disabled={exitSubmitting}
              accessibilityRole="button"
              accessibilityLabel="שלחו לי את הסיכום עכשיו"
            >
              {exitSubmitting ? (
                <ActivityIndicator color="#FAF5EA" size="small" />
              ) : (
                <Text style={styles.exitSubmitButtonText}>שלחו לי את הסיכום עכשיו</Text>
              )}
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors.background,
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors.background,
  },
  header: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Platform.OS === 'web' ? theme.spacing.md : 8,
    paddingVertical: Platform.OS === 'web' ? theme.spacing.sm : 6,
    paddingTop: Platform.OS === 'ios' ? 45 : Platform.OS === 'web' ? theme.spacing.sm : 6,
    backgroundColor: theme.colors.background,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerUserArea: {
    position: 'absolute',
    insetInlineEnd: Platform.OS === 'web' ? theme.spacing.md : 8,
    top: Platform.OS === 'ios' ? 45 : Platform.OS === 'web' ? theme.spacing.sm : 6,
    zIndex: 2,
  },
  logoButton: {
    alignSelf: 'center',
  },
  logoImage: { width: 120, height: 60 },
  messagesWrapper: {
    flex: 1,
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    backgroundColor: theme.colors.background,
  },
  messagesList: {
    flex: 1,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  } as any,
  messagesContent: {
    paddingVertical: theme.spacing.md,
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: Platform.OS === 'web' ? 0 : theme.spacing.sm,
    maxWidth: 900,
    width: '100%',
    minWidth: 0,
    alignSelf: 'center',
  },
  scrollToBottomWrap: { position: 'absolute', right: 12, bottom: 12, zIndex: 50 } as any,
  scrollToBottomBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: theme.colors.primary,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 8,
  },
  scrollToBottomBtnPressed: { opacity: 0.9, transform: [{ scale: 0.98 }] },
  scrollToBottomText: { color: '#FAF5EA', fontSize: 22, fontFamily: theme.fonts.bold, marginTop: -2 },

  resumeLeadGateContainer: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    alignItems: 'center',
    backgroundColor: theme.colors.backgroundLight,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
  },
  resumeLeadGateInner: {
    width: '100%',
    maxWidth: 900,
    minWidth: 0,
  },
  viewCoursesAgainButton: {
    width: '100%',
    backgroundColor: '#3B82F6',
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.sm + 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.xs,
    shadowColor: '#3B82F6',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 6,
  },
  viewCoursesAgainButtonPressed: { opacity: 0.9, transform: [{ scale: 0.99 }] },
  viewCoursesAgainText: { fontSize: 15, fontFamily: theme.fonts.bold, color: '#FAF5EA' },
  resumeLeadGateButton: {
    width: '100%',
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.sm + 2,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: theme.colors.primary,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 6,
  },
  resumeLeadGateButtonPressed: { opacity: 0.9, transform: [{ scale: 0.99 }] },
  resumeLeadGateText: { fontSize: 15, fontFamily: theme.fonts.bold, color: '#FAF5EA' },

  inputWrapper: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors.backgroundLight,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    alignItems: 'center',
    paddingHorizontal: Platform.OS === 'web' ? 0 : theme.spacing.sm,
  } as any,
  inputContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-end',
    paddingHorizontal: Platform.OS === 'web' ? theme.spacing.md : 0,
    paddingVertical: theme.spacing.sm,
    gap: theme.spacing.sm,
    maxWidth: 900,
    width: '100%',
    minWidth: 0,
  },
  inputFieldWrapper: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
  },
  input: {
    width: '100%',
    minWidth: 0,
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.sm + 2,
    paddingHorizontal: theme.spacing.md,
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'start' as any,
    maxHeight: 100,
    borderWidth: 1,
    borderColor: theme.colors.border,
    ...(Platform.OS === 'web' ? { direction: 'rtl' } as any : {}),
  } as any,
  inputDisabled: { opacity: 0.5, backgroundColor: theme.colors.backgroundLight },
  inputAtLimit: { borderColor: theme.colors.error },
  charCounter: {
    position: 'absolute',
    bottom: 4,
    insetInlineEnd: 8,
    fontSize: 10,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  } as any,
  charCounterAtLimit: { color: theme.colors.error },
  sendButton: {
    flexShrink: 0,
    backgroundColor: '#8B5CF6',
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonPressed: { backgroundColor: '#C8531E', transform: [{ scale: 0.95 }] },
  sendButtonDisabled: { backgroundColor: theme.colors.secondary, opacity: 0.5 },
  sendButtonFocused: { borderWidth: 3, borderColor: '#FAF5EA', elevation: 10 },

  disclaimerWrapper: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors.backgroundLight,
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
  },
  disclaimerContainer: {
    paddingHorizontal: 0,
    paddingVertical: theme.spacing.xs,
    alignItems: 'center',
    gap: theme.spacing.xs,
    maxWidth: 900,
    width: '100%',
  },
  disclaimerText: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
  },
  legalLinks: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xs,
    flexWrap: 'wrap',
  },
  legalLinkText: {
    fontSize: 11,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textDecorationLine: 'underline',
  },
  legalSeparator: { fontSize: 11, color: theme.colors.textLight },

  emailButtonContainer: {
    width: '100%',
    paddingHorizontal: theme.spacing.lg,
    paddingTop: 0,
    paddingBottom: theme.spacing.md,
    alignItems: 'center',
  },
  emailButton: {
    maxWidth: '100%',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.borderRadius.md,
    shadowColor: theme.colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  emailButtonPressed: { opacity: 0.8, transform: [{ scale: 0.98 }] },
  emailButtonText: { fontSize: 16, fontFamily: theme.fonts.bold, color: '#FAF5EA' },

  loadingBanner: {
    position: 'absolute',
    bottom: 80,
    alignSelf: 'center',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    backgroundColor: theme.colors.cardBackground,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: theme.colors.border,
    maxWidth: '92%',
  },
  loadingBannerText: { fontSize: 13, fontFamily: theme.fonts.medium, color: theme.colors.text },

  exitOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  exitOverlayTouchable: { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 },
  exitCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: theme.colors.cardBackground,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.lg,
    shadowColor: theme.colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 8,
  },
  exitHeader: { flexDirection: 'row-reverse', justifyContent: 'flex-end', marginBottom: theme.spacing.sm },
  exitTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginBottom: theme.spacing.xs,
  },
  exitSubtitle: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: theme.spacing.md,
  },
  exitButtons: { gap: theme.spacing.sm },
  exitPrimaryButtonRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.md,
  },
  exitPrimaryButtonPressed: { opacity: 0.9, transform: [{ scale: 0.99 }] },
  exitPrimaryButtonText: { fontSize: 15, fontFamily: theme.fonts.bold, color: '#FAF5EA' },
  exitSecondaryButton: {
    backgroundColor: theme.colors.backgroundLight,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.md,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  exitSecondaryButtonPressed: { opacity: 0.9, transform: [{ scale: 0.99 }] },
  exitSecondaryButtonText: { fontSize: 14, fontFamily: theme.fonts.medium, color: theme.colors.text },
  exitInput: {
    backgroundColor: theme.colors.backgroundLight,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'right',
    marginBottom: theme.spacing.sm,
  } as any,
  exitInputError: { borderColor: theme.colors.error, borderWidth: 2 },
  exitCheckboxContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  exitCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: theme.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.backgroundLight,
  },
  exitCheckboxChecked: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
  exitCheckmark: { color: '#FAF5EA', fontSize: 14, fontFamily: theme.fonts.bold },
  exitCheckboxLabel: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    flex: 1,
    textAlign: 'right',
  },
  exitErrorText: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.error,
    textAlign: 'right',
    marginBottom: theme.spacing.xs,
  },
  exitSubmitButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.md,
    alignItems: 'center',
  },
  exitSubmitButtonDisabled: { opacity: 0.6 },
  exitSubmitButtonText: { fontSize: 15, fontFamily: theme.fonts.bold, color: '#FAF5EA' },

  welcomeScroll: {
    flex: 1,
    width: '100%',
  },
  welcomeScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
  },
  welcomeContainer: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'visible',
    alignItems: 'center',
  },
  welcomeTitle: {
    fontSize: 26,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    lineHeight: 36,
    marginBottom: theme.spacing.xs,
    maxWidth: 440,
  },
  welcomeSubtitle: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    lineHeight: 23,
    marginBottom: theme.spacing.lg,
    maxWidth: 400,
  },
  entryChoiceGroup: {
    width: '100%',
    maxWidth: 460,
    gap: theme.spacing.md,
  },
  entryChoiceCard: {
    borderRadius: 0,
    borderWidth: 1,
    borderColor: 'rgba(110, 118, 140, 0.24)',
    backgroundColor: '#FAF5EA',
    paddingVertical: 18,
    paddingHorizontal: 16,
    gap: 6,
  },
  entryChoiceCardPrimary: {
    borderColor: 'rgba(200,83,30,0.18)',
    backgroundColor: '#F6F1FF',
  },
  entryChoiceCardPressed: {
    transform: [{ scale: 0.985 }],
    opacity: 0.92,
  },
  entryChoiceTitle: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: '#0B1020',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  entryChoiceTitlePrimary: {
    color: '#4C1D95',
  },
  entryChoiceText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: theme.fonts.regular,
    color: '#4B5563',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  entryChoiceTextPrimary: {
    color: '#5B21B6',
  },
  quickFlowContainer: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingTop: Platform.OS === 'web' ? theme.spacing.xl : theme.spacing.lg,
    paddingBottom: Platform.OS === 'web' ? theme.spacing.xl : theme.spacing.lg,
    backgroundColor: theme.colors.background,
  },
  quickFlowShell: {
    width: '100%',
    maxWidth: 760,
    borderRadius: 0,
    backgroundColor: '#0D0B1E',
    borderWidth: 1,
    borderColor: 'rgba(167, 113, 255, 0.22)',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 18,
    gap: 16,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0px 0px 40px rgba(200,83,30,0.18), 0px 12px 36px rgba(10, 8, 28, 0.50)' }
      : {
          shadowColor: '#C8531E',
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.18,
          shadowRadius: 20,
          elevation: 8,
        }),
  } as any,
  quickFlowStepIndicatorRow: {
    width: '100%',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
  },
  quickFlowStepIndicatorItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    flex: 1,
  },
  quickFlowStepIndicatorCircle: {
    width: 34,
    height: 34,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(167, 113, 255, 0.25)',
    backgroundColor: 'rgba(200,83,30,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickFlowStepIndicatorCircleActive: {
    borderColor: '#A771FF',
    backgroundColor: 'rgba(167, 113, 255, 0.22)',
    transform: [{ scale: 1.06 }],
  },
  quickFlowStepIndicatorCircleComplete: {
    borderColor: '#9B30FF',
    backgroundColor: '#C8531E',
  },
  quickFlowStepIndicatorText: {
    fontFamily: theme.fonts.bold,
    fontSize: 13,
    color: '#6B5B9E',
  },
  quickFlowStepIndicatorTextActive: {
    color: '#D4BBFF',
  },
  quickFlowStepIndicatorTextComplete: {
    color: '#FAF5EA',
    fontSize: 14,
  },
  quickFlowStepConnector: {
    flex: 1,
    height: 4,
    borderRadius: 999,
    overflow: 'hidden',
    marginHorizontal: 8,
    backgroundColor: 'rgba(200,83,30,0.18)',
  },
  quickFlowStepConnectorFill: {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
  },
  quickFlowStepConnectorFillActive: {
    backgroundColor: '#9B30FF',
  },
  quickFlowBackButton: {
    minWidth: 92,
    paddingHorizontal: 16,
    paddingVertical: 11,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: 'rgba(167, 113, 255, 0.30)',
    backgroundColor: 'rgba(200,83,30,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickFlowSwitchButton: {
    minWidth: 150,
    paddingHorizontal: 16,
    paddingVertical: 11,
    borderRadius: 0,
    backgroundColor: '#15110D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickFlowBackButtonPressed: { opacity: 0.87 },
  quickFlowBackButtonText: { fontFamily: theme.fonts.medium, fontSize: 14, color: '#C4AEFF' },
  quickFlowSwitchButtonText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#FAF5EA' },
  quickFlowQuestionHeader: {
    gap: 8,
    marginBottom: 2,
  },
  quickFlowProgressText: {
    fontSize: 12,
    fontFamily: theme.fonts.medium,
    color: '#9B8AC4',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  quickQuestionCard: {
    width: '100%',
    borderRadius: 0,
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    gap: 16,
  },
  quickQuestionTitle: {
    fontSize: 25,
    lineHeight: 34,
    textAlign: 'right',
    writingDirection: 'rtl',
    fontFamily: theme.fonts.bold,
    color: '#F0EAFF',
  },
  quickOptionsWrap: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 10,
  },
  quickOptionCard: {
    width: '100%',
    minHeight: 58,
    borderRadius: 0,
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderWidth: 1.5,
    borderColor: 'rgba(167, 113, 255, 0.38)',
    backgroundColor: 'rgba(200,83,30,0.18)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0px 0px 12px rgba(200,83,30,0.18)' }
      : {}),
  } as any,
  quickOptionCardPressed: { opacity: 0.88, transform: [{ scale: 0.985 }] },
  quickOptionCardSelected: {
    backgroundColor: 'rgba(167, 113, 255, 0.28)',
    borderColor: '#A771FF',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0px 0px 20px rgba(167, 113, 255, 0.40)' }
      : {}),
  } as any,
  quickOptionText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: '#D4BBFF',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  quickOptionTextSelected: {
    color: '#FAF5EA',
  },
  quickFlowFooter: {
    marginTop: 2,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(167, 113, 255, 0.15)',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
  },
  quickFlowLoadingRow: {
    marginTop: theme.spacing.md,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  },
  quickFlowLoadingText: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: theme.colors.textLight,
  },
  quickFlowErrorText: {
    marginTop: 10,
    fontFamily: theme.fonts.regular,
    color: '#DC2626',
    textAlign: 'center',
  },
  quickFlowResultsBlock: {
    width: '100%',
    maxWidth: 560,
    marginTop: theme.spacing.lg,
    backgroundColor: '#F2EBDD',
    borderRadius: 0,
    padding: 14,
    gap: 8,
    borderWidth: 1,
    borderColor: '#1A1611',
  },
  quickFlowResultsTitle: {
    fontSize: 19,
    fontFamily: theme.fonts.bold,
    color: '#15110D',
    textAlign: 'right',
  },
  quickFlowResultsSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: theme.fonts.regular,
    color: '#3A332A',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  quickFlowResultsButton: {
    marginTop: 6,
    alignSelf: 'stretch',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#15110D',
  },
  quickFlowResultsButtonText: {
    color: '#FAF5EA',
    fontFamily: theme.fonts.bold,
    fontSize: 14,
  },
  messageBlockModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.md,
  },
  messageBlockModalContent: {
    backgroundColor: '#FAF5EA',
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.xl,
    width: '100%',
    maxWidth: 420,
    gap: theme.spacing.md,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.3)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 10 },
          shadowOpacity: 0.25,
          shadowRadius: 20,
          elevation: 15,
        }),
  } as any,
  messageBlockModalTitle: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginBottom: theme.spacing.xs,
  },
  messageBlockModalText: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textMuted,
    textAlign: 'center',
    lineHeight: 22,
  },
  messageBlockModalInput: {
    width: '100%',
    height: 48,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'right',
    backgroundColor: '#F2EBDD',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  } as any,
  messageBlockModalError: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: '#EF4444',
    textAlign: 'center',
  },
  messageBlockModalButton: {
    width: '100%',
    height: 48,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
  },
  messageBlockModalButtonDisabled: {
    opacity: 0.5,
  },
  messageBlockModalButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#FAF5EA',
  },
});
